import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, FileText, Link2, MessageSquare, Search, Settings } from "lucide-react";
import { useSearch, useRecent } from "@/api/hooks";
import type { SearchHit } from "@/api/types";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandDialog } from "@/components/ui/command";

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function groupHits(hits: SearchHit[]): { noteId: string; title: string | null; hits: SearchHit[] }[] {
  const map = new Map<string, { noteId: string; title: string | null; hits: SearchHit[] }>();
  for (const h of hits) {
    const g = map.get(h.noteId) ?? { noteId: h.noteId, title: h.title, hits: [] };
    g.hits.push(h);
    map.set(h.noteId, g);
  }
  return [...map.values()];
}

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const dq = useDebounced(q.trim(), 180);
  const search = useSearch(dq, undefined, 20);
  const recent = useRecent();

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const go = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  const groups = groupHits(search.data ?? []);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <Command shouldFilter={!dq}>
        <CommandInput placeholder="Search notes or jump to…" value={q} onValueChange={setQ} />
        <CommandList>
          {dq ? (
            <>
              {search.isFetching && groups.length === 0 && <div className="px-3 py-4 text-sm text-muted-foreground">Searching…</div>}
              {!search.isFetching && groups.length === 0 && <CommandEmpty>No notes match “{dq}”.</CommandEmpty>}
              {groups.map((g) => (
                <CommandGroup key={g.noteId} heading={g.title || "Untitled"}>
                  {g.hits.slice(0, 3).map((h, i) => (
                    <CommandItem key={`${h.noteId}-${h.blockId ?? i}`} value={`${h.noteId}-${h.blockId ?? i}`} onSelect={() => go(`/notes/${h.noteId}${h.blockId ? `#${h.blockId}` : ""}`)}>
                      <FileText />
                      <span className="line-clamp-2 text-[13px] text-muted-foreground">{h.snippet || g.title}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
              {groups.length > 0 && (
                <CommandGroup>
                  <CommandItem value="see-all" onSelect={() => go(`/search?q=${encodeURIComponent(dq)}`)}>
                    <Search />
                    See all results for “{dq}”
                  </CommandItem>
                </CommandGroup>
              )}
            </>
          ) : (
            <>
              <CommandGroup heading="Go to">
                <CommandItem value="recent" onSelect={() => go("/")}>
                  <Clock /> Recent
                </CommandItem>
                <CommandItem value="threads" onSelect={() => go("/threads")}>
                  <MessageSquare /> Threads
                </CommandItem>
                <CommandItem value="search page" onSelect={() => go("/search")}>
                  <Search /> Search
                </CommandItem>
                <CommandItem value="api tokens settings" onSelect={() => go("/settings/tokens")}>
                  <Settings /> API tokens
                </CommandItem>
                <CommandItem value="connect client settings" onSelect={() => go("/settings/connect")}>
                  <Link2 /> Connect a client
                </CommandItem>
              </CommandGroup>
              {recent.data && recent.data.notes.length > 0 && (
                <CommandGroup heading="Recently changed">
                  {recent.data.notes.slice(0, 6).map((n) => (
                    <CommandItem key={n.id} value={`note ${n.title ?? ""} ${n.id}`} onSelect={() => go(`/notes/${n.id}`)}>
                      <FileText /> {n.title || "Untitled"}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </>
          )}
        </CommandList>
      </Command>
    </CommandDialog>
  );
}
